import { chartTooltipTitle } from "@/lib/progressUtils";

// Shared tooltip renderer — one style for all charts
export function makeTooltip({ viewMode, cursorYear, unit, lang = "ar" }) {
  return function GenericTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload || {};
    const title = chartTooltipTitle(p, viewMode, cursorYear, lang);
    return (
      <div className="bg-white rounded-xl shadow-lg px-3 py-2 text-xs" dir={lang === "en" ? "ltr" : "rtl"}>
        <div className="font-bold text-neutral-900 mb-1">{title}</div>
        <div className="space-y-0.5">
          {payload.map((row) => (
            <div key={row.dataKey} className="flex items-center gap-2 justify-between">
              <span className="text-neutral-500">{row.name || row.dataKey}</span>
              <span className="num font-bold" style={{ color: row.color }}>
                {Math.round(row.value || 0)} {unit || ""}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };
}

// Balance-chart-specific tooltip that reveals the FULL calculation formula
// so the user understands where each daily number came from.
export function makeBalanceTooltip({ viewMode, cursorYear, tdee, lang = "ar" }) {
  const en = lang === "en";
  return function BalanceTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload || {};
    const title = chartTooltipTitle(p, viewMode, cursorYear, lang);
    const calIn  = Math.round(p.cal_in  || 0);
    const calOut = Math.round(p.cal_out || 0);
    const bal    = Math.round(p.balance || 0);
    const dayTdee = Number(p.tdee) > 0 ? Number(p.tdee) : tdee;
    const goodColor = "#10B981";
    const badColor  = "#EF4444";
    const neutralColor = "#F59E0B";
    const balColor = bal < -50 ? goodColor : bal > 50 ? badColor : neutralColor;

    if (!p.has_meals) {
      return (
        <div className="bg-white rounded-2xl shadow-xl border-[1.5px] border-black px-3 py-2 text-xs w-[220px]" dir={en ? "ltr" : "rtl"}>
          <div className="font-bold text-neutral-900 mb-1">{title}</div>
          <div className="text-[11px] text-neutral-500 leading-relaxed">
            {en ? "No meals logged this day — not counted in the balance" : "لا يوجد تسجيل طعام في هذا اليوم — غير محسوب في الفارق"}
          </div>
        </div>
      );
    }

    if (p.is_today) {
      return (
        <div className="bg-white rounded-2xl shadow-xl border-[1.5px] border-black px-3 py-2.5 text-xs w-[240px]" dir={en ? "ltr" : "rtl"}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-neutral-900">{title}</span>
            <span className="text-[9px] bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-bold">{en ? "In progress" : "قيد الإكمال"}</span>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between"><span className="text-neutral-600">{en ? "Intake" : "المتناول"}</span><span className="num font-bold text-neutral-800" dir="ltr">{calIn}</span></div>
            <div className="flex justify-between"><span className="text-neutral-600">{en ? "Body burn (TDEE)" : "حرق الجسم (TDEE)"}</span><span className="num font-bold text-amber-600" dir="ltr">−{dayTdee}</span></div>
            <div className="flex justify-between"><span className="text-neutral-600">{en ? "Workouts" : "التمارين"}</span><span className="num font-bold text-red-500" dir="ltr">−{calOut}</span></div>
            <div className="border-t border-neutral-200 my-1"></div>
            <div className="flex justify-between text-[13px]"><span className="font-bold text-neutral-800">{en ? "Balance so far" : "الفارق حتى الآن"}</span><span className="num font-black" style={{ color: balColor }} dir="ltr">{bal > 0 ? `+${bal}` : bal}</span></div>
          </div>
          <div className="text-[10px] text-neutral-400 mt-1.5 leading-relaxed">{en ? "Today isn't finished — the number will update with your remaining meals" : "اليوم لم ينته — الرقم سيتغيّر مع بقية وجباتك"}</div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-2xl shadow-xl border-[1.5px] border-black px-3 py-2.5 text-xs w-[240px]" dir={en ? "ltr" : "rtl"}>
        <div className="font-bold text-neutral-900 mb-2">{title}</div>
        <div className="space-y-1">
          <div className="flex justify-between">
            <span className="text-neutral-600">{en ? "Intake" : "المتناول"}</span>
            <span className="num font-bold text-neutral-800" dir="ltr">{calIn}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-neutral-600">{en ? "Body burn (TDEE)" : "حرق الجسم (TDEE)"}</span>
            <span className="num font-bold text-amber-600" dir="ltr">−{dayTdee}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-neutral-600">{en ? "Workouts" : "التمارين"}</span>
            <span className="num font-bold text-red-500" dir="ltr">−{calOut}</span>
          </div>
          <div className="border-t border-neutral-200 my-1"></div>
          <div className="flex justify-between text-[13px]">
            <span className="font-bold text-neutral-800">{en ? "Balance" : "الفارق"}</span>
            <span className="num font-black" style={{ color: balColor }} dir="ltr">{bal > 0 ? `+${bal}` : bal}</span>
          </div>
          <div className="text-[10px] text-neutral-500 mt-1.5 leading-relaxed" dir="ltr">
            {calIn} − {dayTdee} − {calOut} = {bal}
          </div>
        </div>
      </div>
    );
  };
}

// Custom tooltip for macro chart — uses shared title format
export function makeMacroTooltip({ viewMode, cursorYear, lang = "ar" }) {
  const en = lang === "en";
  return function MT({ active, payload }) {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload || {};
    return (
      <div className="bg-white rounded-xl shadow-lg px-3 py-2 text-xs" dir={en ? "ltr" : "rtl"}>
        <div className="font-bold text-neutral-900 mb-1">{chartTooltipTitle(p, viewMode, cursorYear, lang)}</div>
        <div className="space-y-0.5">
          <div className="flex items-center gap-1.5 justify-between">
            <span className="text-neutral-500">{en ? "Protein" : "بروتين"}</span>
            <span className="num font-semibold text-blue-500">{Math.round(p.protein_g || 0)} g</span>
          </div>
          <div className="flex items-center gap-1.5 justify-between">
            <span className="text-neutral-500">{en ? "Carbs" : "كارب"}</span>
            <span className="num font-semibold text-emerald-500">{Math.round(p.carbs_g || 0)} g</span>
          </div>
          <div className="flex items-center gap-1.5 justify-between">
            <span className="text-neutral-500">{en ? "Fat" : "دهون"}</span>
            <span className="num font-semibold text-orange-500">{Math.round(p.fat_g || 0)} g</span>
          </div>
          <div className="flex items-center gap-1.5 justify-between pt-1 border-t border-neutral-100 mt-1">
            <span className="text-neutral-500">{en ? "Calories" : "السعرات"}</span>
            <span className="num font-bold text-neutral-900">{Math.round(p.cal_in || 0)}</span>
          </div>
        </div>
      </div>
    );
  };
}
